import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Mail, MapPin, Phone } from 'lucide-react';
import { formatTenantAddress } from '../../types/tenants';
import EntryActionsMenu from './EntryActionsMenu';
import { useTenants } from '../../hooks/tenants/useTenants';
import { useUserNames } from '../../Helpers/useUserNames';
import { ParkingQueueMutations, UseParkingQueueReturn } from '../../hooks/parkingQueue';
import { describeWaited, QueueRowFacts } from '../../Helpers/parkingQueueUtils';
import { ParkingQueueEntry, QueueLogEvent, QUEUE_CLOSE_REASON_LABELS } from '../../types/parkingQueue';
import { SpotTarget } from '../../api/parkingQueueApi';
import CloseEntryModal from './CloseEntryModal';
import QueueLeaseFlow from './QueueLeaseFlow';
import { describeLogEvent, PII_HINT, wantedAreasText, wishesText, internalText } from './queueLabels';
import { leasesPath } from '../../Helpers/leaseUtils';

interface Props {
    show: boolean;
    onHide: () => void;
    row: QueueRowFacts;
    queue: UseParkingQueueReturn;
    mutations: ParkingQueueMutations;
    onEdit: (entry: ParkingQueueEntry) => void;
    /** The board's `?spot=`, preselected in Nytt hyresavtal. */
    spot?: SpotTarget;
}

interface LogProps {
    entry: ParkingQueueEntry;
    buildingName: (buildingKey: string) => string;
    /** Applicant name for an entry key (the entries a deviation passed over). */
    entryName: (entryKey: string) => string;
    /** The building a lease key belongs to, for the lease link; undefined when the lease is not loaded. */
    leaseBuilding: (leaseKey: string) => string | undefined;
}

/**
 * The entry's history, collapsed by default behind a quiet toggle and newest
 * first when opened. Lease and deviation events are filled in: the spot, who
 * was passed over, a link to the lease. Actor names are resolved only once it
 * is expanded.
 */
export const ParkingQueueLog: React.FC<LogProps> = ({ entry, buildingName, entryName, leaseBuilding }) => {
    const [open, setOpen] = useState(false);
    const { loadUserNames, userNames } = useUserNames();
    const events = useMemo(() => [...entry.log].reverse(), [entry.log]);

    const describe = (event: QueueLogEvent): React.ReactNode => {
        switch (event.type) {
            case 'deviation': {
                const ahead = Object.keys(event.aheadEntryKeys ?? {}).map(entryName).join(', ');
                return `Avvikelse från standardordningen: gick före ${event.to ?? '?'} sökande${ahead ? ` (${ahead})` : ''}${event.from ? ` · ${event.from}` : ''}`;
            }
            case 'leaseLinked': {
                const buildingKey = event.to ? leaseBuilding(event.to) : undefined;
                const spot = `${buildingKey ? `${buildingName(buildingKey)} · ` : ''}${event.from ?? ''}`;
                return (
                    <>Hyresavtal kopplat{spot ? ` (${spot})` : ''}{buildingKey && event.to && <> · <Link to={leasesPath(buildingKey, event.to)}>Visa hyresavtal</Link></>}</>
                );
            }
            default:
                return describeLogEvent(event);
        }
    };
    useEffect(() => {
        if (open) void loadUserNames(entry.log.map(e => e.actor));
    }, [open, entry.log, loadUserNames]);

    return (
        <div className="parking-queue-log-section">
            <button type="button" className="parking-queue-log-toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Logg ({events.length} {events.length === 1 ? 'händelse' : 'händelser'})
            </button>
            {open && (
                <ul className="parking-queue-log">
                    {events.map(event => (
                        <li key={event.key}>
                            <span className="parking-queue-log-meta">{event.when.slice(0, 16)} · {userNames[event.actor] ?? '…'}</span>
                            <span>{describe(event)}</span>
                            {event.note && <span className="parking-queue-log-note">{event.note}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const ParkingQueueEntryDetailModal: React.FC<Props> = ({ show, onHide, row, queue, mutations, onEdit, spot }) => {
    const { entry } = row;
    const { getTenantName, tenantsByKey } = useTenants();
    const [contactOpen, setContactOpen] = useState(false);
    const tenant = tenantsByKey[entry.tenantKey];
    const address = tenant ? formatTenantAddress(tenant) : null;
    const hasContact = !!tenant && !!(tenant.email || tenant.phone || address || tenant.orgNumber);
    const [noteOpen, setNoteOpen] = useState(false);
    const [note, setNote] = useState('');
    const [showLease, setShowLease] = useState(false);
    const [showClose, setShowClose] = useState(false);
    const active = entry.status === 'active';
    const wishes = wishesText(row);

    const entryName = (entryKey: string) => getTenantName(queue.entries.find(e => e.key === entryKey)?.tenantKey);
    const leaseBuilding = (leaseKey: string) => queue.leases.find(l => l.key === leaseKey)?.buildingKey;

    return (
        <>
            <Modal show={show && !showLease && !showClose} onHide={onHide} size="lg">
                <Modal.Header closeButton>
                    <div className="parking-queue-detail-title">
                        <Modal.Title as="h5">{getTenantName(entry.tenantKey)}</Modal.Title>
                        {hasContact && (
                            <button type="button" className="parking-queue-contact-toggle" aria-expanded={contactOpen} aria-controls="parking-queue-contact"
                                onClick={() => setContactOpen(o => !o)}>
                                {contactOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                {contactOpen ? 'Dölj kontaktuppgifter' : 'Visa kontaktuppgifter'}
                            </button>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {contactOpen && tenant && (
                        <div id="parking-queue-contact" className="parking-queue-contact">
                            {tenant.email && <div><Mail size={14} /><a href={`mailto:${tenant.email}`}>{tenant.email}</a></div>}
                            {tenant.phone && <div><Phone size={14} /><a href={`tel:${tenant.phone.replace(/\s/g, '')}`}>{tenant.phone}</a></div>}
                            {address && <div><MapPin size={14} /><span>{address}</span></div>}
                            {tenant.orgNumber && <div className="text-muted">Org.nr {tenant.orgNumber}</div>}
                        </div>
                    )}
                    {mutations.error && <Alert variant="danger">{mutations.error}</Alert>}
                    <dl className="parking-queue-facts">
                        <dt>Status</dt><dd>{active ? 'I kö' : `Avslutad${entry.closeReason ? ` (${QUEUE_CLOSE_REASON_LABELS[entry.closeReason]})` : ''}${entry.closeNote ? ` – ${entry.closeNote}` : ''}`}</dd>
                        <dt>Intern/extern</dt><dd>{internalText(row, queue.buildingName)}</dd>
                        <dt>Platser idag</dt><dd>{row.heldSpots.length === 0 ? 'Inga' : row.heldSpots.map(s => `${queue.buildingName(s.buildingKey)} · ${s.label}`).join(', ')}</dd>
                        <dt>Ködatum</dt><dd>{entry.queueDate} ({describeWaited(row.daysWaited)})</dd>
                        <dt>Önskemål</dt><dd>{wantedAreasText(entry.wantedAreas, queue)}{wishes.length > 0 && <div className="text-muted">{wishes.join(' · ')}</div>}</dd>
                        <dt>Kommentar</dt><dd>{entry.comment || '–'}</dd>
                        <dt>Senast rörd</dt><dd>{row.lastTouched}</dd>
                    </dl>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        {active && <Button size="sm" variant="primary" onClick={() => { mutations.clearError(); setShowLease(true); }}>Nytt hyresavtal…</Button>}
                        <Button size="sm" variant="outline-secondary" onClick={() => setNoteOpen(o => !o)}>Anteckning</Button>
                        <div className="ms-auto">
                            <EntryActionsMenu active={active} disabled={mutations.submitting}
                                onEdit={() => onEdit(entry)} onClose={() => setShowClose(true)} onReopen={() => mutations.reopenEntry(entry.key)} />
                        </div>
                    </div>
                    {noteOpen && (
                        <Form.Group className="mb-3">
                            <Form.Control as="textarea" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="T.ex. Ringde, fortfarande intresserad" />
                            <Form.Text muted>{PII_HINT}</Form.Text>
                            <div className="mt-2">
                                <Button size="sm" disabled={!note.trim() || mutations.submitting}
                                    onClick={async () => { await mutations.addNote(entry.key, note.trim()); setNote(''); setNoteOpen(false); }}>Spara anteckning</Button>
                            </div>
                        </Form.Group>
                    )}
                    <ParkingQueueLog entry={entry} buildingName={queue.buildingName} entryName={entryName} leaseBuilding={leaseBuilding} />
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={onHide}>Stäng</Button></Modal.Footer>
            </Modal>
            {showLease && <QueueLeaseFlow entry={entry} queue={queue} mutations={mutations} initialSpot={spot} onHide={() => setShowLease(false)} />}
            {showClose && <CloseEntryModal show onHide={() => setShowClose(false)} entry={entry} mutations={mutations} />}
        </>
    );
};

export default ParkingQueueEntryDetailModal;
